import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isAdminRole } from "@/lib/accountRole";
import { api, formatError } from "@/lib/api";
import { useInboxUnread } from "@/context/InboxUnreadContext";
import { toast } from "@/lib/toast";
import { openFeedback } from "@/components/FeedbackModal";
import { AlertTriangle, Star, ChevronRight, ShieldAlert, Loader2 } from "lucide-react";

const AMBER = "#C2600F";
const GOLD = "#E8C46A";

// OpticSwap marketing links — smaller, muted, condensed (wrapped chips), ALWAYS last.
const OPTICSWAP_LINKS = [
  ["About", "/about"], ["Pricing", "/pricing"], ["FAQ", "/faq"], ["User Map", "/map"],
  ["Ambassadors", "/ambassadors"], ["In the wild", "/in-the-wild"], ["Swag", "/swag"], ["Forum", "/forum"], ["Partners", "/partners"], ["Community", "/community"],
];

function Avatar({ src, username }) {
  const initials = (username || "?").slice(0, 2).toUpperCase();
  if (src) return <img src={src} alt="" className="w-11 h-11 object-cover border border-sniper shrink-0" />;
  return <div className="w-11 h-11 shrink-0 bg-olive/20 border border-olive/40 flex items-center justify-center font-heading text-sm text-olive-bright">{initials}</div>;
}

export default function MobileMenu({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const admin = isAdminRole(user?.role);
  const { total: unread } = useInboxUnread();
  const [ctx, setCtx] = useState({ founding: null, orders: 0, savedEntitled: false });
  const [resending, setResending] = useState(false);

  // Live context, fetched fresh each time the menu OPENS (counts + founding read from source,
  // never cached) so the numbers a member sees are current the moment they open the drawer.
  // The unread messages count comes from the app-wide InboxUnread context so it's always live
  // (it also refreshes on foreground), not only when this drawer opens.
  useEffect(() => {
    if (!open || !user) return;
    let active = true;
    (async () => {
      const out = { founding: null, orders: 0, savedEntitled: false };
      try { const { data } = await api.get("/founding/prompt"); out.founding = data; } catch { /* founding row just hides */ }
      try {
        const { data } = await api.get("/orders/mine");
        out.orders = (data.purchases || []).filter((o) => o.status === "shipped").length
                   + (data.sales || []).filter((o) => o.status === "held").length;
      } catch { /* count hides */ }
      try { const { data } = await api.get("/me/saved-searches"); out.savedEntitled = data.entitled !== false; } catch { /* row hides */ }
      if (active) setCtx(out);
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.user_id]);

  // Contained scroll: while the drawer is open, LOCK the page behind it (body.mobile-drawer-open
  // in App.css) and give the drawer its OWN bounded, momentum scroll so a swipe moves the MENU,
  // not the marketplace page underneath. maxH is measured from the drawer's top to the viewport
  // bottom so the last items (Sign out) are always reachable, even under the admin mode-band.
  const panelRef = useRef(null);
  const [maxH, setMaxH] = useState(null);
  useEffect(() => {
    if (!open) return;
    document.body.classList.add("mobile-drawer-open");
    const measure = () => {
      const el = panelRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setMaxH(Math.max(180, Math.round(window.innerHeight - top)));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      document.body.classList.remove("mobile-drawer-open");
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [open, user]);

  const panelStyle = { maxHeight: maxH ? `${maxH}px` : undefined, WebkitOverflowScrolling: "touch", touchAction: "pan-y" };
  const panelCls = "md:hidden border-t border-sniper bg-surface max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain";

  if (!open) return null;

  const go = (to) => { onClose(); navigate(to); };
  const doFeedback = () => { onClose(); openFeedback(); };
  const handleLogout = () => { onClose(); logout(); navigate("/"); };
  const resend = async () => {
    setResending(true);
    try { await api.post("/auth/send-verification"); toast.success("Verification email sent — check your inbox (and spam)."); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setResending(false); }
  };

  const Group = ({ title, testid, children }) => (
    <div className="px-4 pt-3 pb-1" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/80 select-none">{title}</div>
      <div className="flex flex-col mt-0.5">{children}</div>
    </div>
  );
  const Item = ({ to, onClick, children, count = 0, testid, tone }) => {
    const cls = `flex items-center justify-between gap-3 py-2.5 font-mono uppercase text-sm transition-colors ${tone === "volt" ? "text-volt hover:text-volt/80" : "text-secondary-foreground hover:text-foreground"}`;
    const body = (
      <>
        <span>{children}</span>
        {count > 0 && <span className="font-mono text-xs text-muted-foreground tabular-nums shrink-0" data-testid={`${testid}-count`}>{count}</span>}
      </>
    );
    return to
      ? <Link to={to} onClick={onClose} data-testid={testid} className={cls}>{body}</Link>
      : <button onClick={onClick} data-testid={testid} className={`${cls} w-full text-left`}>{body}</button>;
  };

  // ---- LOGGED OUT: marketing + auth only ----
  if (!user) {
    return (
      <div ref={panelRef} style={panelStyle} className={`${panelCls} pb-4`} data-testid="nav-mobile-menu">
        <Group title="OpticSwap" testid="m-group-opticswap">
          <div className="grid grid-cols-2 gap-x-6" data-testid="m-opticswap-grid">
            {OPTICSWAP_LINKS.map(([label, to]) => (
              <Link key={to} to={to} onClick={onClose} data-testid={`m-mkt-${to.slice(1)}`} className="flex items-center py-2.5 font-mono uppercase text-sm text-secondary-foreground hover:text-foreground transition-colors">{label}</Link>
            ))}
          </div>
        </Group>
        <div className="border-t border-sniper mt-2 px-4 pt-3 flex flex-col gap-1">
          <Link to="/join" onClick={onClose} data-testid="m-nav-login" className="py-2 font-mono uppercase text-sm text-secondary-foreground hover:text-foreground">Log in</Link>
          <Link to="/join" onClick={onClose} data-testid="m-nav-register" className="py-2.5 text-center bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase text-sm transition-colors">Create account</Link>
        </div>
      </div>
    );
  }

  const emailUnverified = user.email_verified === false;
  const f = ctx.founding;
  const showFounding = !!(f && f.eligible && !f.has_seat && (f.public_remaining || 0) > 0);
  const showPayouts = !!(user.stripe_account_id || user.stripe_payouts_enabled); // Connect onboarding started
  const showSaved = ctx.savedEntitled;

  return (
    <div ref={panelRef} style={panelStyle} className={`${panelCls} pb-3`} data-testid="nav-mobile-menu">
      {/* 1. VERIFICATION BANNER — only while unverified; disappears the instant they verify */}
      {emailUnverified && (
        <div data-testid="m-verify-banner" className="mx-3 mt-3 border-l-4 px-3 py-3" style={{ borderLeftColor: AMBER, background: "rgba(194,96,15,0.10)" }}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#E8A05A" }} />
            <div className="min-w-0">
              <p className="font-mono text-[12px] leading-snug" style={{ color: "#E8A05A" }}>Verify your email to buy, sell and message.</p>
              <button onClick={resend} disabled={resending} data-testid="m-verify-resend"
                className="mt-1.5 inline-flex items-center gap-1.5 font-mono uppercase text-[11px] font-bold tracking-wide underline underline-offset-2 disabled:opacity-60" style={{ color: "#E8A05A" }}>
                {resending && <Loader2 className="w-3 h-3 animate-spin" />} Resend the email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. IDENTITY ROW — avatar, @handle, MY PROFILE & BADGES → */}
      <button onClick={() => go("/profile")} data-testid="m-identity-row"
        className="w-full flex items-center gap-3 px-4 py-3 border-b border-sniper text-left hover:bg-white/[0.03] transition-colors">
        <Avatar src={user.avatar_thumb || user.avatar} username={user.username} />
        <div className="min-w-0">
          <div className="font-heading text-foreground truncate">@{user.username}</div>
          <div className="font-mono uppercase text-[11px] tracking-wide text-olive-bright inline-flex items-center gap-1 mt-0.5">My profile &amp; badges <ChevronRight className="w-3.5 h-3.5" /></div>
        </div>
      </button>

      {/* 3. FOUNDING SEAT ROW — gold, live remaining from the ledger; only if claimable + seatless */}
      {showFounding && (
        <button onClick={() => go("/founding")} data-testid="m-founding-row"
          className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-sniper text-left hover:bg-[#E8C46A]/10 transition-colors" style={{ borderLeftWidth: 4, borderLeftColor: GOLD }}>
          <span className="font-mono uppercase text-sm inline-flex items-center gap-2" style={{ color: GOLD }}><Star className="w-4 h-4 fill-current" /> Claim your founding seat</span>
          <span className="font-mono text-xs tabular-nums shrink-0" data-testid="m-founding-remaining" style={{ color: GOLD }}>{f.public_remaining} left</span>
        </button>
      )}

      {/* Admin quick entry (staff only) */}
      {admin && (
        <button onClick={() => go("/admin")} data-testid="m-admin"
          className="w-full flex items-center gap-2 px-4 py-2.5 border-b border-sniper text-left font-mono uppercase text-sm text-red-400 hover:bg-red-500/10 transition-colors">
          <ShieldAlert className="w-4 h-4" /> Admin portal
        </button>
      )}

      {/* 4. GROUPED SECTIONS — member items first, marketing last */}
      <Group title="Selling" testid="m-group-selling">
        <Item to="/create" testid="m-item-post" tone="volt">Post a listing</Item>
        <Item to="/dashboard#listings" testid="m-item-my-listings">My listings</Item>
        <Item to="/orders" testid="m-item-orders" count={ctx.orders}>Orders</Item>
        {showPayouts && <Item to="/settings/payouts" testid="m-item-payouts">Payouts</Item>}
      </Group>

      <Group title="Buying" testid="m-group-buying">
        <Item to="/feed" testid="m-item-browse">Marketplace</Item>
        {showSaved && <Item to="/feed#saved" testid="m-item-saved-searches">Saved searches</Item>}
        <Item to="/inbox" testid="m-item-messages" count={unread}>Messages</Item>
      </Group>

      <Group title="Account" testid="m-group-account">
        <Item to="/dashboard" testid="m-item-dashboard">Dashboard</Item>
        <Item to="/settings/profile" testid="m-item-settings">Settings &amp; security</Item>
        <Item onClick={doFeedback} testid="m-item-feedback">Give feedback</Item>
      </Group>

      <Group title="OpticSwap" testid="m-group-opticswap">
        <div className="grid grid-cols-2 gap-x-6" data-testid="m-opticswap-grid">
          {OPTICSWAP_LINKS.map(([label, to]) => (
            <Link key={to} to={to} onClick={onClose} data-testid={`m-mkt-${to.slice(1)}`}
              className="flex items-center py-2.5 font-mono uppercase text-sm text-secondary-foreground hover:text-foreground transition-colors">{label}</Link>
          ))}
        </div>
      </Group>

      {/* 5. LOG OUT — separated by a rule */}
      <div className="border-t border-sniper mt-2 px-4 pt-2">
        <button onClick={handleLogout} data-testid="m-logout" className="w-full text-left py-2.5 font-mono uppercase text-sm text-blaze hover:text-blaze/80 transition-colors">Log out</button>
      </div>
    </div>
  );
}
